import React, {useEffect, useState} from "react";
import {useNavigate, useSearchParams, Link} from "react-router-dom";
import {
    Box,
    Button,
    CssBaseline,
    Stack,
    Card as MuiCard,
    Typography,
    Alert,
    LinearProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Lottie from "lottie-react";
import anime10 from "../animations/anime10.json";

const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: "auto",
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    [theme.breakpoints.up("sm")]: {
        width: "500px"
    },
    ...theme.applyStyles('dark', {
        background: 'rgba(30, 30, 30, 0.9)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
}));

const ActivateContainer = styled(Stack)(({ theme }) => ({
    minHeight: "100vh",
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(4)
    },
    "&::before": {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, rgba(52, 152, 219, 0.1) 0%, rgba(155, 89, 182, 0.1) 100%)',
        zIndex: 0,
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '25px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
    fontSize: '2.2rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
    marginBottom: '8px',
}));

const SubTitle = styled(Typography)(({ theme }) => ({
    color: '#7f8c8d',
    textAlign: 'center',
    fontSize: '16px',
    lineHeight: '1.5',
    marginBottom: '16px',
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    '&:hover': {
        color: '#764ba2',
        textDecoration: 'underline',
    },
}));

const CountdownText = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    color: '#7f8c8d',
    textAlign: 'center',
    marginTop: '8px',
}));

export default function ActivatePage() {
    const [status, setStatus] = useState("pending");
    const [message, setMessage] = useState("");
    const [countdown, setCountdown] = useState(3);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const token = searchParams.get("token");
        console.log(token);

        if (token) {
            fetch(`${process.env.REACT_APP_API_URL}/activate?token=${token}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })
                .then(async (res) => {
                    if (res.ok) {
                        setStatus("success");
                        setMessage("Hesabınız başarıyla aktifleştirildi! 🎉");

                        // Geri sayım başlat
                        const timer = setInterval(() => {
                            setCountdown(prev => {
                                if (prev <= 1) {
                                    clearInterval(timer);
                                    navigate("/signin");
                                    return 0;
                                }
                                return prev - 1;
                            });
                        }, 1000);

                        return () => clearInterval(timer);
                    } else {
                        throw new Error("Aktivasyon başarısız oldu.");
                    }
                })
                .catch((error) => {
                    console.error("Hata:", error.message);
                    setStatus("error");
                    setMessage("Aktivasyon bağlantısı geçersiz veya süresi dolmuş.");
                });
        } else {
            setStatus("error");
            setMessage("Aktivasyon tokenı bulunamadı.");
        }
    }, [searchParams, navigate]);

    const getStatusIcon = () => {
        switch (status) {
            case "pending":
                return "⏳";
            case "success":
                return "✅";
            case "error":
                return "❌";
            default:
                return "⏳";
        }
    };

    const getAlertSeverity = () => {
        switch (status) {
            case "success":
                return "success";
            case "error":
                return "error";
            default:
                return "info";
        }
    };

    return (
        <div className="ActivatePage">
            <CssBaseline enableColorScheme />
            <ActivateContainer direction="column" justifyContent="center">
                <Card variant="outlined">
                    <PageTitle>{getStatusIcon()} Hesap Aktivasyonu</PageTitle>

                    {status === "pending" && (
                        <SubTitle>
                            Aktivasyon işleminiz gerçekleştiriliyor...
                        </SubTitle>
                    )}

                    <Lottie
                        animationData={anime10}
                        loop={status === "pending"}
                        style={{
                            width: status === "pending" ? '80%' : '60%',
                            height: status === "pending" ? '80%' : '60%',
                            margin: '0 auto'
                        }}
                    />

                    {status === "pending" && (
                        <LinearProgress
                            sx={{
                                width: '100%',
                                borderRadius: '10px',
                                height: '8px',
                                '& .MuiLinearProgress-bar': {
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                }
                            }}
                        />
                    )}

                    {status !== "pending" && (
                        <Alert
                            severity={getAlertSeverity()}
                            sx={{
                                borderRadius: '12px',
                                width: '100%',
                                textAlign: 'center',
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            {message}
                        </Alert>
                    )}

                    {status === "success" && countdown > 0 && (
                        <>
                            <CountdownText>
                                {countdown} saniye sonra giriş sayfasına yönlendirileceksiniz...
                            </CountdownText>
                            <LinearProgress
                                variant="determinate"
                                value={(3 - countdown) * 33.33}
                                sx={{
                                    width: '100%',
                                    borderRadius: '10px',
                                    height: '6px',
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                    }
                                }}
                            />
                        </>
                    )}

                    {status === "error" && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                            <StyledButton
                                fullWidth
                                variant="contained"
                                onClick={() => navigate("/signin")}
                            >
                                🔑 Giriş Sayfasına Dön
                            </StyledButton>

                            <Typography sx={{ color: "gray", fontSize: '14px', textAlign: 'center' }}>
                                Hesabınız yok mu? <StyledLink to="/signup">Kayıt Ol</StyledLink>
                            </Typography>
                        </Box>
                    )}

                    {status === "success" && (
                        <Typography sx={{ color: "gray", fontSize: '14px', textAlign: 'center' }}>
                            Beklemek istemiyorsanız{" "}
                            <StyledLink to="/signin">buraya tıklayın</StyledLink>
                        </Typography>
                    )}
                </Card>
            </ActivateContainer>
        </div>
    );
}
